import { useCallback, useEffect, useState } from 'react'
import type { FormEvent } from 'react'
import { Link } from 'react-router'
import { api, ApiError, post } from '../lib/api'
import type { AlertRule, AlertSettings, CompanyHit, DeliveryLog, Watchlist } from '../lib/types'
import { Button, Card, CATEGORIES, EmptyState, Skeleton, SymbolPicker, useToast } from '../ui'
import { Page } from './Page'
import { disablePush, enablePush, pushStatus } from '../lib/pwa'
import styles from './p6.module.css'

interface RulesResp { data: AlertRule[]; limits: { rules: number; mode: string; channels: string[] } }
const SCOPE_LABEL: Record<AlertRule['scope'], string> = {
  symbol: 'One stock', watchlist: 'One watchlist', watchlists: 'All my watchlists', holdings: 'My holdings', all: 'All companies', filters: 'Saved filters',
}
const RULE_CATEGORIES = ['order_win', 'financial_results', 'dividend', 'bonus', 'stock_split', 'buyback', 'credit_rating', 'management_change',
  'merger_acquisition', 'fund_raising', 'capacity_expansion', 'open_offer', 'pledge', 'insider_trading', 'regulatory_warning', 'litigation', 'board_meeting']
const STATUS_TONE: Record<string, string | undefined> = { sent: styles.pillUp, failed: styles.pillDown, skipped: styles.pillGrey, queued: styles.pillWarn }
const REASON: Record<string, string> = {
  smtp_not_configured: 'email not set up yet', quiet_hours: 'quiet hours', dedup_30min: 'duplicate within 30 min', telegram_not_linked: 'Telegram not linked',
  telegram_disabled: 'Telegram paused', email_off: 'email alerts off', push_not_enabled: 'browser notifications off',
}
const when = (iso: string) => new Intl.DateTimeFormat('en-IN', { timeZone: 'Asia/Kolkata', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', hour12: false }).format(new Date(iso))

function describe(r: AlertRule, lists: Watchlist[]): string {
  const parts = [r.scope === 'symbol' ? r.symbol : r.scope === 'watchlist' ? `List: ${lists.find(l => l.id === r.watchlist_id)?.name ?? 'deleted list'}` : SCOPE_LABEL[r.scope]]
  parts.push(r.categories.length ? r.categories.map(c => CATEGORIES[c]?.label ?? c).join(', ') : 'all notable filings')
  if (r.min_impact > 0) parts.push(`impact ≥ ${r.min_impact}`)
  if (r.directions.length) parts.push(r.directions.map(d => ({ up: '▲ positive', down: '▼ negative', mixed: 'mixed', neutral: 'neutral' }[d] ?? d)).join(' / '))
  return parts.join(' · ')
}

function NewRuleForm({ lists, limits, onCreated }: { lists: Watchlist[]; limits: RulesResp['limits']; onCreated: () => void }) {
  const toast = useToast()
  const [scope, setScope] = useState<'holdings' | 'watchlists' | 'watchlist' | 'symbol' | 'all'>('holdings')
  const [symbol, setSymbol] = useState<CompanyHit | null>(null)
  const [listId, setListId] = useState<number | ''>(lists[0]?.id ?? '')
  const [cats, setCats] = useState<string[]>([])
  const [minImpact, setMinImpact] = useState(60)
  const [dirs, setDirs] = useState<string[]>([])
  const [channels, setChannels] = useState<string[]>(limits.channels.includes('telegram') ? ['telegram'] : ['email'])
  const [busy, setBusy] = useState(false)
  const toggle = (arr: string[], v: string, set: (a: string[]) => void) => set(arr.includes(v) ? arr.filter(x => x !== v) : [...arr, v])

  const submit = async (e: FormEvent) => {
    e.preventDefault()
    if (scope === 'symbol' && !symbol) { toast.show({ tone: 'error', message: 'Pick a stock' }); return }
    setBusy(true)
    try {
      const r = await post<{ downgraded: string | null }>('/alert-rules', {
        scope, ...(scope === 'symbol' ? { symbol: symbol!.symbol } : {}), ...(scope === 'watchlist' ? { watchlist_id: Number(listId) } : {}),
        categories: cats, min_impact: minImpact, ...(dirs.length ? { directions: dirs } : {}), channels,
      })
      toast.show({ tone: 'success', message: r.downgraded ? 'Rule saved. On your plan it arrives in the daily digest.' : 'Alert rule saved' })
      setCats([]); setDirs([]); setSymbol(null)
      onCreated()
    } catch (err) { toast.show({ tone: 'error', message: err instanceof ApiError ? err.message : 'Could not save' }) } finally { setBusy(false) }
  }

  return (
    <form className={styles.form} onSubmit={submit}>
      <label className={styles.field}><span>Alert me about</span>
        <select className={styles.select} value={scope} onChange={e => setScope(e.target.value as typeof scope)}>
          <option value="holdings">My holdings</option>
          <option value="watchlists">All my watchlists</option>
          <option value="watchlist" disabled={!lists.length}>One watchlist</option>
          <option value="symbol">One stock</option>
          <option value="all">All companies</option>
        </select>
      </label>
      {scope === 'symbol' && (symbol
        ? <div className={styles.row}><span className={styles.sym}>{symbol.symbol}</span><span className={styles.muted}>{symbol.name}</span><Button size="sm" variant="ghost" onClick={() => setSymbol(null)}>Change</Button></div>
        : <SymbolPicker label="Stock for this alert" onSelect={setSymbol} placeholder="Search a stock…" />)}
      {scope === 'watchlist' && (
        <label className={styles.field}><span>Watchlist</span>
          <select className={styles.select} value={listId} onChange={e => setListId(Number(e.target.value))}>{lists.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}</select>
        </label>
      )}
      <fieldset className={styles.fieldset}>
        <legend>Filing types (none selected = all notable filings)</legend>
        {RULE_CATEGORIES.map(c => <label key={c} className={styles.check}><input type="checkbox" checked={cats.includes(c)} onChange={() => toggle(cats, c, setCats)} />{CATEGORIES[c]?.label ?? c}</label>)}
      </fieldset>
      <label className={styles.field}><span>Minimum impact score: {minImpact}</span>
        <input type="range" min={0} max={95} step={5} value={minImpact} onChange={e => setMinImpact(Number(e.target.value))} aria-valuetext={`${minImpact}`} />
      </label>
      <fieldset className={styles.fieldset}>
        <legend>Direction (none = any)</legend>
        {([['up', '▲ Positive'], ['down', '▼ Negative'], ['mixed', 'Mixed']] as const).map(([v, l]) => <label key={v} className={styles.check}><input type="checkbox" checked={dirs.includes(v)} onChange={() => toggle(dirs, v, setDirs)} />{l}</label>)}
      </fieldset>
      <fieldset className={styles.fieldset}>
        <legend>Send to</legend>
        {['telegram', 'email', 'push'].map(ch => (
          <label key={ch} className={styles.check}>
            <input type="checkbox" checked={channels.includes(ch)} disabled={!limits.channels.includes(ch)} onChange={() => toggle(channels, ch, setChannels)} />
            {ch === 'telegram' ? 'Telegram' : ch === 'push' ? 'Browser notification' : 'Email'}{!limits.channels.includes(ch) ? ' (Pro)' : ''}
          </label>
        ))}
      </fieldset>
      {limits.mode !== 'instant' && <div className={styles.upgrade} role="note">On the Free plan alerts arrive in the 08:45 pre-open digest. Pro sends them the moment a filing lands.<Link to="/pricing">See plans</Link></div>}
      <div><Button type="submit" variant="primary" loading={busy} disabled={busy || !channels.length}>Save alert rule</Button></div>
    </form>
  )
}

function PushCard() {
  const toast = useToast()
  const [state, setState] = useState<'unsupported' | 'denied' | 'on' | 'off' | null>(null)
  const [busy, setBusy] = useState(false)
  useEffect(() => { pushStatus().then(setState).catch(() => setState('unsupported')) }, [])
  const toggle = async () => {
    setBusy(true)
    try {
      if (state === 'on') { await disablePush(); setState('off'); toast.show({ tone: 'success', message: 'Browser notifications off' }) }
      else if (await enablePush()) { setState('on'); toast.show({ tone: 'success', message: 'Browser notifications on for this device' }) }
      else setState(await pushStatus())
    } catch (e) { toast.show({ tone: 'error', message: e instanceof ApiError ? e.message : 'Could not change notifications' }) } finally { setBusy(false) }
  }
  const test = async () => {
    try { await post('/push/test', {}); toast.show({ tone: 'success', message: 'Test notification sent' }) }
    catch (e) { toast.show({ tone: 'error', message: e instanceof ApiError ? e.message : 'Could not send' }) }
  }
  return (
    <Card title="Browser notifications">
      {state === null ? <Skeleton lines={1} label="Loading" /> : state === 'unsupported' ? <p className={styles.text2}>This browser doesn't support notifications. On iPhone, add BCEIE to your Home Screen first.</p>
        : state === 'denied' ? <p className={styles.text2}>Notifications are blocked for this site. Allow them in your browser settings, then come back.</p>
        : (
          <div className={styles.form}>
            <p className={styles.text2}>{state === 'on' ? 'On for this device. Choose "Browser notification" on a rule to use it.' : 'Get alerts as notifications on this phone or computer, even when BCEIE is closed.'}</p>
            <div className={styles.row}><Button size="sm" variant={state === 'on' ? 'ghost' : 'primary'} onClick={toggle} loading={busy}>{state === 'on' ? 'Turn off on this device' : 'Turn on notifications'}</Button>
              {state === 'on' && <Button size="sm" onClick={test}>Send test</Button>}</div>
          </div>
        )}
    </Card>
  )
}

export function AlertsPage() {
  const toast = useToast()
  const [settings, setSettings] = useState<AlertSettings | null>(null)
  const [rules, setRules] = useState<RulesResp | null>(null)
  const [lists, setLists] = useState<Watchlist[]>([])
  const [log, setLog] = useState<DeliveryLog[] | null>(null)
  const [link, setLink] = useState<string | null>(null)
  const [quiet, setQuiet] = useState<{ start: string; end: string }>({ start: '', end: '' })

  const load = useCallback(() => {
    api<AlertSettings>('/alerts/settings').then(s => { setSettings(s); setQuiet({ start: s.preferences.quiet_start ?? '', end: s.preferences.quiet_end ?? '' }) }).catch(() => undefined)
    api<RulesResp>('/alert-rules').then(setRules).catch(() => undefined)
    api<{ data: Watchlist[] }>('/watchlists').then(r => setLists(r.data)).catch(() => undefined)
    api<{ data: DeliveryLog[] }>('/alerts/log?limit=30').then(r => setLog(r.data)).catch(() => setLog([]))
  }, [])
  useEffect(load, [load])

  const savePrefs = async (patch: Record<string, unknown>, ok = 'Saved') => {
    try { const r = await api<{ preferences: AlertSettings['preferences'] }>('/alerts/settings', { method: 'PUT', body: JSON.stringify(patch) }); setSettings(s => s && { ...s, preferences: r.preferences }); toast.show({ tone: 'success', message: ok }) }
    catch (e) { toast.show({ tone: 'error', message: e instanceof ApiError ? e.message : 'Could not save' }) }
  }
  const linkTelegram = async () => {
    try { const r = await post<{ url: string | null }>('/alerts/telegram/link', {}); setLink(r.url); if (r.url) window.open(r.url, '_blank', 'noopener') }
    catch (e) { toast.show({ tone: 'error', message: e instanceof ApiError ? e.message : 'Could not create a link' }) }
  }
  const sendTest = async (channel: 'telegram' | 'email') => {
    try {
      const r = await post<{ status: string; error: string | null }>('/alerts/test', { channel })
      toast.show({ tone: r.status === 'sent' ? 'success' : 'error', message: r.status === 'sent' ? `Test sent to ${channel === 'telegram' ? 'Telegram' : 'your email'}` : `Not sent: ${REASON[r.error ?? ''] ?? r.error}` })
      load()
    } catch (e) { toast.show({ tone: 'error', message: e instanceof ApiError ? e.message : 'Could not send' }) }
  }
  const patchRule = async (id: number, patch: Partial<AlertRule>) => {
    try { await api(`/alert-rules/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }); load() } catch (e) { toast.show({ tone: 'error', message: e instanceof ApiError ? e.message : 'Could not update' }) }
  }
  const deleteRule = async (id: number) => {
    try { await api(`/alert-rules/${id}`, { method: 'DELETE' }); load() } catch { toast.show({ tone: 'error', message: 'Could not delete' }) }
  }

  const atLimit = !!rules && rules.data.length >= rules.limits.rules
  return (
    <Page title="Alerts" subtitle="Telegram and email alerts for new filings on your stocks, plus the 08:45 pre-open digest and Saturday recap">
      <div className={styles.grid}>
        <div className={styles.col}>
          <Card title={rules ? `Alert rules (${rules.data.length} of ${rules.limits.rules})` : 'Alert rules'} variant="flush">
            {!rules ? <div className={styles.pad}><Skeleton variant="row" lines={3} label="Loading rules" /></div>
              : rules.data.length === 0 ? <EmptyState title="No alert rules yet" compact>Create one below — for example, every notable filing on your holdings.</EmptyState>
              : (
                <ul className={styles.list} aria-label="Your alert rules">
                  {rules.data.map(r => (
                    <li key={r.id} className={styles.item}>
                      <div className={styles.grow}>
                        <div className={styles.row}>
                          <span className={styles.riskTitle}>{r.name}</span>
                          <span className={`${styles.pill} ${r.mode === 'instant' ? '' : styles.pillGrey}`}>{r.mode === 'instant' ? 'Instant' : 'Digest'}</span>
                          {r.channels.map(c => <span key={c} className={`${styles.pill} ${styles.pillGrey}`}>{c === 'telegram' ? 'Telegram' : c === 'push' ? 'Browser' : 'Email'}</span>)}
                          {r.migrated && <span className={`${styles.pill} ${styles.pillWarn}`} title="Copied from the current BCEIE app; it takes over when the new app goes live">From current app · starts at switch-over</span>}
                        </div>
                        <span className={styles.riskDetail}>{describe(r, lists)}</span>
                      </div>
                      <label className={styles.check}><input type="checkbox" checked={r.is_active} onChange={e => patchRule(r.id, { is_active: e.target.checked })} />On</label>
                      <button type="button" className={styles.iconBtn} aria-label={`Delete rule ${r.name}`} onClick={() => deleteRule(r.id)}>✕</button>
                    </li>
                  ))}
                </ul>
              )}
          </Card>
          <Card title="New alert rule">
            {!rules ? <Skeleton lines={3} label="Loading" /> : atLimit
              ? <div className={styles.upgrade} role="note">You have {rules.limits.rules} rules, the most your plan allows.<Link to="/pricing">See plans</Link></div>
              : <NewRuleForm lists={lists} limits={rules.limits} onCreated={load} />}
          </Card>
          <Card title="Recent alerts" variant="flush" action={<span className={styles.muted}>last 30</span>}>
            {!log ? <div className={styles.pad}><Skeleton variant="row" lines={3} label="Loading alert log" /></div>
              : log.length === 0 ? <EmptyState title="Nothing sent yet" compact>Alerts and digests sent to you will be listed here.</EmptyState>
              : (
                <div className={styles.tableWrap} tabIndex={0} role="region" aria-label="Recent alert deliveries (scrolls sideways)">
                  <table className={styles.table}>
                    <caption className="sr-only">Recent alert deliveries</caption>
                    <thead><tr><th scope="col">When</th><th scope="col">What</th><th scope="col">Channel</th><th scope="col">Status</th></tr></thead>
                    <tbody>
                      {log.map(d => (
                        <tr key={d.id}>
                          <td className={styles.num} style={{ textAlign: 'left' }}>{when(d.created_at)}</td>
                          <td>{d.symbol ? <><Link to={`/stock/${encodeURIComponent(d.symbol)}?tab=filings`} className={styles.sym}>{d.symbol}</Link> <span className={styles.riskDetail}>{d.headline}</span></> : <span className={styles.riskDetail}>{d.kind === 'digest' ? 'Digest' : d.kind === 'test' ? 'Test message' : '—'}</span>}</td>
                          <td>{d.channel === 'telegram' ? 'Telegram' : d.channel === 'push' ? 'Browser' : 'Email'}</td>
                          <td><span className={`${styles.pill} ${STATUS_TONE[d.status] ?? ''}`}>{d.status}</span>{d.error && <span className={styles.muted}> {REASON[d.error] ?? d.error}</span>}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
          </Card>
        </div>
        <div className={styles.col}>
          <Card title="Telegram">
            {!settings ? <Skeleton lines={2} label="Loading" /> : settings.telegram.linked ? (
              <div className={styles.form}>
                <p className={styles.text2}>Linked{settings.telegram.username ? ` as @${settings.telegram.username}` : ''}. {settings.telegram.enabled ? 'Alerts are on.' : 'Alerts are paused (send /start to the bot to resume).'}</p>
                <div><Button size="sm" onClick={() => sendTest('telegram')}>Send test message</Button></div>
              </div>
            ) : (
              <div className={styles.form}>
                <p className={styles.text2}>Link Telegram to get alerts as they happen. Tap the button, then press <strong>Start</strong> in Telegram.</p>
                <div className={styles.row}><Button variant="primary" onClick={linkTelegram} disabled={!settings.telegram.bot}>Link Telegram</Button>{link && <a href={link} target="_blank" rel="noopener noreferrer">Open the bot</a>}</div>
                {link && <p className={styles.muted}>The link works for 15 minutes. <button type="button" className={styles.clear} onClick={load}>I've pressed Start — refresh</button></p>}
              </div>
            )}
          </Card>
          <PushCard />
          <Card title="Email">
            {!settings ? <Skeleton lines={2} label="Loading" /> : (
              <div className={styles.form}>
                <p className={styles.text2}>{settings.email.address}</p>
                {!settings.email.configured && <p className={styles.muted}>Email delivery is not connected yet — alerts to email are logged but not sent.</p>}
                <label className={styles.check}><input type="checkbox" checked={settings.preferences.email_alerts} onChange={e => savePrefs({ email_alerts: e.target.checked })} />Send alerts and digests by email</label>
                <div><Button size="sm" onClick={() => sendTest('email')}>Send test email</Button></div>
              </div>
            )}
          </Card>
          <Card title="Digests and quiet hours">
            {!settings ? <Skeleton lines={3} label="Loading" /> : (
              <div className={styles.form}>
                <label className={styles.check}><input type="checkbox" checked={settings.preferences.digest_preopen} onChange={e => savePrefs({ digest_preopen: e.target.checked })} />08:45 pre-open digest (trading days)</label>
                <label className={styles.check}><input type="checkbox" checked={settings.preferences.digest_weekly} onChange={e => savePrefs({ digest_weekly: e.target.checked })} />Saturday weekly recap</label>
                <form className={styles.form} onSubmit={e => { e.preventDefault(); void savePrefs(quiet.start && quiet.end ? { quiet_start: quiet.start, quiet_end: quiet.end } : { quiet_start: null, quiet_end: null }, 'Quiet hours saved') }}>
                  <p className={styles.text2}>Quiet hours (IST): instant alerts are held and appear in the next digest.</p>
                  <div className={styles.row}>
                    <label className={styles.field}><span>From</span><input className={styles.input} type="time" value={quiet.start} onChange={e => setQuiet(q => ({ ...q, start: e.target.value }))} /></label>
                    <label className={styles.field}><span>To</span><input className={styles.input} type="time" value={quiet.end} onChange={e => setQuiet(q => ({ ...q, end: e.target.value }))} /></label>
                  </div>
                  <div className={styles.row}><Button size="sm" type="submit">Save quiet hours</Button>{(settings.preferences.quiet_start || quiet.start) && <Button size="sm" variant="ghost" onClick={() => { setQuiet({ start: '', end: '' }); void savePrefs({ quiet_start: null, quiet_end: null }, 'Quiet hours off') }}>Turn off</Button>}</div>
                </form>
              </div>
            )}
          </Card>
        </div>
      </div>
    </Page>
  )
}
